import { useEffect, useState } from 'react';

import { ApplicationProductName } from 'enums/common.enums';
import { hilAutomatedOffersApi } from 'api/AutomatedOffersApi';
import { posAutomatedOffersApi } from 'api/SmallLoanProgram/AutomatedOffersApi';
import { offersApi } from 'api/OffersApi';
import { getMaxLoanAmount } from 'prequalificationV2/pages/Dashboard/AcceptedOfferDetails/acceptOfferDetails.utils';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { CreateAccountView } from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/createAccount.enums';
import {
  getCreateAccountView,
  getLowestRate,
} from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/createAccount.utils';
import {
  CreateAccountOffersSummary,
  GetCreateAccountOffersSummaryParams,
} from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/createAccount.types';
import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';

const getOffersSummary = async ({
  applicationId,
  productName,
  hasAutomatedOffer,
  hasMultipleLoanOffers,
  loanAmount,
}: GetCreateAccountOffersSummaryParams): Promise<CreateAccountOffersSummary> => {
  if (productName === ApplicationProductName.SmallLoanProgram) {
    const { loanOffers, maxLoanAmount } = await posAutomatedOffersApi.getOffers({
      applicationId: applicationId,
      loanAmount: loanAmount ?? 0,
    });

    return { maxLoanAmount: maxLoanAmount, lowestRate: getLowestRate(loanOffers) };
  }

  if (hasAutomatedOffer) {
    const { loanOffers, maxLoanAmount } = await hilAutomatedOffersApi.getOffers({
      applicationId: applicationId,
      loanAmount: loanAmount ?? 0,
    });

    return { maxLoanAmount: maxLoanAmount, lowestRate: getLowestRate(loanOffers) };
  }

  // Manual pitch: offers come from the manual offers endpoint and the max is the product range max
  // (the same value used for the "Cash To You" slider on the manual flow).
  const loanOffers = hasMultipleLoanOffers
    ? (await offersApi.getMultipleTermOffers(applicationId)).loanOffers
    : await offersApi.getOneTermOffers(applicationId);

  return { maxLoanAmount: getMaxLoanAmount(productName ?? ''), lowestRate: getLowestRate(loanOffers) };
};

export const useCreateAccountOffersSummary = (): CreateAccountOffersSummary => {
  const dispatch = useAppDispatch();
  const { applicationId, variables, productName, applicationStatus } = useAppSelector((state) => state.application);
  const view = getCreateAccountView({
    hasAutomatedOffer: variables?.hasAutomatedOffer,
    applicationStatus: applicationStatus,
    productName: productName,
    loanAmount: variables?.loan_amount,
    posHighAmountOfferResult: variables?.pos_high_amount_offer_result,
  });

  const [summary, setSummary] = useState<{ maxLoanAmount?: number; lowestRate?: number }>({});

  useEffect(() => {
    if (view !== CreateAccountView.OffersAvailable || !applicationId) {
      return;
    }

    const loadSummary = async () => {
      try {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const result = await getOffersSummary({
          applicationId: applicationId,
          productName: productName,
          hasAutomatedOffer: variables?.hasAutomatedOffer,
          hasMultipleLoanOffers: variables?.multiple_loan_offers,
          loanAmount: variables?.loan_amount,
        });

        setSummary(result);
      } catch (error) {
        // The banner degrades gracefully without the numbers, so a failed fetch is non-blocking.
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    };

    loadSummary();
  }, [view, applicationId]);

  return { maxLoanAmount: summary.maxLoanAmount, lowestRate: summary.lowestRate, view: view };
};
